import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { setTokens, exchangeOAuthCode, claimProject } from '../api/client'
import type { FetchError } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { oauthErrorMessages, takeOAuthFlow } from '../components/oauthFlow'

export default function OAuthCallback() {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const exchangedRef = useRef(false)

  useEffect(() => {
    // Guard against StrictMode double-fire — OAuth codes are single-use
    if (exchangedRef.current) return
    exchangedRef.current = true

    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const accessToken = searchParams.get('access_token')
    const refreshToken = searchParams.get('refresh_token')
    const errParam = searchParams.get('error')

    if (errParam) {
      setError(errParam)
      return
    }

    const installationId = searchParams.get('installation_id')
    const setupAction = searchParams.get('setup_action')

    // GitHub App post-install callback: has installation_id but no state (not a login flow).
    // The user is already authenticated — just forward them to the reviews page.
    if (installationId && setupAction === 'install' && !state) {
      navigate(`/reviews?installation_id=${installationId}&setup_action=install`, { replace: true })
      return
    }

    if (code && state) {
      // Read invite code from sessionStorage (set by OAuthProviders during preview mode)
      const previewInviteCode = sessionStorage.getItem('preview_invite_code') || undefined
      sessionStorage.removeItem('preview_invite_code')

      // Which provider redirected here, and whether this is a "connect to my
      // account" flow (set before redirecting; defaults to GitHub)
      const flow = takeOAuthFlow()

      // Exchange the code for tokens (or a link) via the backend
      exchangeOAuthCode(code, state, flow.provider, previewInviteCode)
        .then(async (data) => {
          if (data.linked) {
            navigate(`/profile?connected=${encodeURIComponent(data.provider)}`, { replace: true })
            return
          }
          setTokens(data.access_token, data.refresh_token)
          await refreshUser()

          // Claim any pending anonymous project (from homepage "Sign up to save")
          const pendingProjectId = sessionStorage.getItem('pending_claim_project')
          if (pendingProjectId) {
            sessionStorage.removeItem('pending_claim_project')
            try {
              await claimProject(pendingProjectId)
              navigate(`/projects/${pendingProjectId}`, { replace: true })
              return
            } catch (e) {
              console.warn('Failed to claim project:', e)
            }
          }

          // GitHub App installation combined with OAuth login — forward to reviews
          if (installationId && setupAction === 'install') {
            navigate(`/reviews?installation_id=${installationId}&setup_action=install`, { replace: true })
            return
          }
          navigate('/dashboard', { replace: true })
        })
        .catch((err: FetchError) => {
          // Known failures carry a stable code: show them where the user can act
          // (settings for a connect flow, the login page otherwise)
          const errCode = typeof err?.data?.code === 'string' ? err.data.code : ''
          if (errCode && oauthErrorMessages[errCode]) {
            const target = flow.link ? `/profile?oauth_error=${errCode}` : `/login?error=${errCode}`
            navigate(target, { replace: true })
            return
          }
          setError(err instanceof Error ? err.message : 'OAuth authentication failed')
        })
    } else if (accessToken && refreshToken) {
      // Legacy flow: tokens provided directly via query params
      setTokens(accessToken, refreshToken)
      refreshUser().then(async () => {
        const pendingProjectId = sessionStorage.getItem('pending_claim_project')
        if (pendingProjectId) {
          sessionStorage.removeItem('pending_claim_project')
          try {
            await claimProject(pendingProjectId)
            navigate(`/projects/${pendingProjectId}`, { replace: true })
            return
          } catch (e) {
            console.warn('Failed to claim project:', e)
          }
        }
        navigate('/dashboard', { replace: true })
      })
    } else {
      setError('Missing parameters in OAuth callback')
    }
  }, [searchParams, navigate, refreshUser])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="rounded-md bg-red-50 p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Authentication Failed</h2>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  )
}
